import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { useFormat } from '@/shared/i18n/use-format'
import type { DatePickerProps } from '@/shared/ui'

import { labelText, type SearchFilterField } from './definition'
import type { FieldFilter } from './search-state'

/** The date picker's strings, for a field named `fieldName`. */
export function usePickerLabels(fieldName: string): DatePickerProps['labels'] {
  const intl = useIntl()
  return useMemo(
    () => ({
      title: intl.formatMessage({ id: 'search.date.title' }),
      previous: intl.formatMessage({ id: 'search.date.previous' }),
      next: intl.formatMessage({ id: 'search.date.next' }),
      cancel: intl.formatMessage({ id: 'search.date.cancel' }),
      ok: intl.formatMessage({ id: 'search.date.ok' }),
      clear: intl.formatMessage({ id: 'search.filters.clearField' }, { field: fieldName }),
    }),
    [intl, fieldName],
  )
}

/** Says what an applied filter does, such as "Last Name contains smi", for its chip. */
export function useDescribeFilter(
  fields: readonly SearchFilterField[],
): (filter: FieldFilter) => string {
  const intl = useIntl()
  const format = useFormat()

  return useCallback(
    (filter: FieldFilter) => {
      const field = fields.find((candidate) => candidate.id === filter.field)
      const name = field === undefined ? filter.field : labelText(intl, field)
      const operator = intl.formatMessage({ id: `search.operator.${filter.operator}` })
      const shown = (value: string) => {
        if (field?.kind === 'date' && /^\d{4}-\d{2}-\d{2}$/u.test(value)) {
          return format.date(value)
        }
        const option = field?.options?.find((candidate) => candidate.value === value)
        return option === undefined ? value : labelText(intl, option)
      }
      const [first = '', second = '', third = ''] = filter.values

      switch (filter.operator) {
        case 'isNull':
        case 'isNotNull':
          return intl.formatMessage({ id: 'search.chip.empty' }, { field: name, operator })
        case 'within':
        case 'notWithin':
          return intl.formatMessage(
            { id: 'search.chip.period' },
            {
              field: name,
              operator,
              direction: intl.formatMessage({ id: `search.period.${first === 'next' ? 'next' : 'last'}` }),
              amount: second,
              unit: intl.formatMessage({ id: `search.unit.${third}` }, { count: Number(second) }),
            },
          )
        case 'between':
        case 'notBetween': {
          const negated = filter.operator === 'notBetween'
          if (first !== '' && second !== '') {
            return intl.formatMessage(
              { id: 'search.chip.range' },
              { field: name, operator, from: shown(first), to: shown(second) },
            )
          }
          return first === ''
            ? intl.formatMessage({ id: negated ? 'search.chip.over' : 'search.chip.atMost' }, { field: name, value: shown(second) })
            : intl.formatMessage({ id: negated ? 'search.chip.under' : 'search.chip.atLeast' }, { field: name, value: shown(first) })
        }
        default:
          return intl.formatMessage(
            { id: 'search.chip.value' },
            {
              field: name,
              operator,
              value: new Intl.ListFormat(intl.locale, { type: 'disjunction' }).format(
                filter.values.filter((value) => value !== '').map(shown),
              ),
            },
          )
      }
    },
    [fields, intl, format],
  )
}
